import {ChangeDetectionStrategy, Component, computed, DestroyRef, EventEmitter, inject, OnInit, signal} from '@angular/core';
import {Event as RouterEvent, NavigationCancel, NavigationEnd, Router, RouterLink, RouterLinkActive} from "@angular/router";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AngularSvgIconModule} from "angular-svg-icon";

import {APP_DOCUMENTS, APP_ENTITIES, APP_INVOICES, APP_MESSAGES, APP_REQUESTS, APP_TASKS} from "../../config/app-urls";
import {UserSession} from "@shared/models/user.session.model";
import {UserSessionService} from "@shared/services/user-session.service";
import {BlurWhenDirective} from "@directives/blur-when.directive";

interface NavigationItem {
    id: string;
    description: string;
    routerLinkCommands: string[];
    isAuthorized: (userSession: UserSession) => boolean;
}

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, RouterLinkActive, AngularSvgIconModule, BlurWhenDirective]
})
export class SidebarComponent implements OnInit {

    readonly isVisible = signal(false);
    hasNavigated: EventEmitter<void> = new EventEmitter();
    readonly navigationItems = signal<NavigationItem[]>([]);
    private readonly _logoUrl = signal<string | null>(null);

    readonly logoUrl = computed(() => {
        const brandingLogo = (globalThis as typeof globalThis & { __brandingLogo?: string }).__brandingLogo;
        if (brandingLogo) {
            return brandingLogo;
        }
        return this._logoUrl();
    });

    private readonly NAVIGATION_ITEMS: NavigationItem[] = [
        {
            id: 'clients',
            description: 'Entities',
            routerLinkCommands: APP_ENTITIES.toRouterLinkCommands(),
            isAuthorized: (userSession: UserSession) => userSession.authorizations.entities
        },
        {
            id: 'invoices',
            description: 'Invoices',
            routerLinkCommands: APP_INVOICES.toRouterLinkCommands(),
            isAuthorized: (userSession: UserSession) => userSession.authorizations.invoices
        },
        {
            id: 'documents',
            description: 'Documents',
            routerLinkCommands: APP_DOCUMENTS.toRouterLinkCommands(),
            isAuthorized: (userSession: UserSession) => userSession.authorizations.documents
        },
        {
            id: 'messages',
            description: 'Messages',
            routerLinkCommands: APP_MESSAGES.toRouterLinkCommands(),
            isAuthorized: (userSession: UserSession) => userSession.authorizations.messages
        },
        {
            id: 'tasks',
            description: 'Tasks',
            routerLinkCommands: APP_TASKS.toRouterLinkCommands(),
            isAuthorized: (userSession: UserSession) => userSession.authorizations.tasks
        },
        {
            id: 'requests',
            description: 'Requests',
            routerLinkCommands: APP_REQUESTS.toRouterLinkCommands(),
            isAuthorized: (userSession: UserSession) => userSession.authorizations.requests
        }
    ];

    private readonly _router = inject(Router);
    private readonly _userSessionService = inject(UserSessionService);
    private readonly _destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        const userSession = this._userSessionService.userSession;
        this.navigationItems.set(this.NAVIGATION_ITEMS.filter(item => item.isAuthorized(userSession)));

        // Load branding logo URL
        this._logoUrl.set((globalThis as typeof globalThis & { __brandingLogo?: string }).__brandingLogo || null);

        this._router.events.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((event: RouterEvent) => {
            this.close();
            if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
                this.hasNavigated.emit();
            }
        });
    }

    open(): void {
        this.isVisible.set(true);
    }

    close(): void {
        this.isVisible.set(false);
    }
}
